import { useMemo } from "react";
import NotArrivedIcon from "@mui/icons-material/Close";
import ArrivedIcon from "@mui/icons-material/Done";
import {
  Avatar,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import type { TOptions } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { dayjs, Prettify } from "@thalamos/common";
import type { IncidentWorkItemWithContext } from "@thalamos/vision-common";
import { getWorkflowTranslationContext, tk } from "@thalamos/vision-common";

import { createRoute } from "../../routing/createRoute.js";
import { TypographyI18N } from "../TypographyI18N/TypographyI18N.js";

export type PlacesOfSafetyProps = {
  /**
   * The latest place of safety, which is the first in the list.
   */
  latestPlaceOfSafety?: Prettify<
    Omit<PlaceOfSafetyProps, "isLatest" | "onEditPos" | "tOptions">
  >;

  /**
   * The other places of safety, which are already sorted by arrival date descending.
   */
  otherPlacesOfSafety?: Array<
    Prettify<Omit<PlaceOfSafetyProps, "isLatest" | "tOptions">>
  >;

  /**
   * The incident work item, used to navigate to the edit page and derive translation context.
   */
  incidentWorkItem?: IncidentWorkItemWithContext;

  /**
   * Whether the user can edit the places of safety.
   */
  canEditPOS: boolean;
};

export const PlacesOfSafety = ({
  latestPlaceOfSafety,
  otherPlacesOfSafety,
  incidentWorkItem,
  canEditPOS,
}: PlacesOfSafetyProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tOptions: TOptions = useMemo(
    () => ({ context: getWorkflowTranslationContext({ incidentWorkItem }) }),
    [incidentWorkItem],
  );

  if (latestPlaceOfSafety === undefined || otherPlacesOfSafety === undefined) {
    return (
      <Stack gap={2}>
        <Typography>
          {t("reviewPlacesOfSafety.placesOfSafety", tOptions)}
        </Typography>
        <PlaceOfSafetySkeleton />
      </Stack>
    );
  }

  const incidentWorkItemId = incidentWorkItem?.incidentWorkItem.id;

  const onEditPos = (formId: string) => {
    if (incidentWorkItemId !== undefined) {
      navigate(
        createRoute.incidentRecordRetrospectivePlaceOfSafetyEditPage(
          incidentWorkItemId,
          formId,
        ),
      );
    }
  };

  return (
    <Stack gap={2}>
      <TypographyI18N
        translationKey={tk("reviewPlacesOfSafety.placesOfSafety")}
        translationOptions={tOptions}
      />
      <TypographyI18N
        translationKey={tk("reviewPlacesOfSafety.latestPlaceOfSafety")}
        translationOptions={tOptions}
      />
      <PlaceOfSafety
        {...latestPlaceOfSafety}
        isLatest={true}
        onEditPos={onEditPos}
        canEditPOS={canEditPOS}
        tOptions={tOptions}
      />
      <TypographyI18N
        translationKey={tk("reviewPlacesOfSafety.otherPlacesOfSafety")}
        translationOptions={tOptions}
      />
      {otherPlacesOfSafety.map((placeOfSafety) => (
        <PlaceOfSafety
          key={placeOfSafety.formId}
          {...placeOfSafety}
          isLatest={false}
          onEditPos={onEditPos}
          canEditPOS={canEditPOS}
          tOptions={tOptions}
        />
      ))}
      {otherPlacesOfSafety.length === 0 && (
        <TypographyI18N
          translationKey={tk("reviewPlacesOfSafety.noOtherPlacesOfSafety")}
          translationOptions={tOptions}
          variant="subtitle2"
          color="text.secondary"
          marginLeft={6}
        />
      )}
    </Stack>
  );
};

export type PlaceOfSafetyProps = {
  formId: string;
  name: string;
  arrivalDateTime?: dayjs.Dayjs;
  createdDateTime: dayjs.Dayjs;
  isLatest: boolean;
  onEditPos?: (formId: string) => void;
  canEditPOS: boolean;
  tOptions?: TOptions;
};

export const PlaceOfSafety = ({
  formId,
  name,
  arrivalDateTime,
  isLatest,
  onEditPos,
  canEditPOS,
  tOptions,
}: PlaceOfSafetyProps) => {
  const { t } = useTranslation();

  return (
    <Stack direction={"row"} spacing={1} alignItems="center">
      <Avatar
        sx={{
          bgcolor:
            isLatest && arrivalDateTime !== undefined
              ? "success.main"
              : undefined,
          alignSelf: "start",
        }}
      >
        {arrivalDateTime === undefined ? <NotArrivedIcon /> : <ArrivedIcon />}
      </Avatar>

      <Stack flexGrow={1}>
        <Typography variant="subtitle2">{name}</Typography>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography
            variant="subtitle2"
            color={isLatest && arrivalDateTime ? "success.main" : undefined}
          >
            {arrivalDateTime === undefined
              ? t("reviewPlacesOfSafety.arrivalNone", tOptions)
              : t("reviewPlacesOfSafety.arrivalDate", {
                  ...tOptions,
                  date: arrivalDateTime
                    .tz("Europe/London")
                    .format("DD/MM/YYYY HH:mm"),
                })}
          </Typography>

          {onEditPos && canEditPOS && (
            <Button
              variant="text"
              size="small"
              onClick={() => {
                onEditPos(formId);
              }}
              sx={{ alignSelf: "flex-end" }}
            >
              {t("reviewPlacesOfSafety.edit")}
            </Button>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};

export type PlaceOfSafetySkeletonProps = Record<string, unknown>;

const TitleSkeleton = () => (
  <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50%" }} />
);
const PosSkeleton = () => (
  <Stack direction={"row"} spacing={1} alignItems="center">
    <Skeleton variant="circular" width={40} height={40} />
    <Stack flexGrow={1}>
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
    </Stack>
  </Stack>
);

export const PlaceOfSafetySkeleton = (_props: PlaceOfSafetySkeletonProps) => {
  return (
    <Stack gap={2}>
      <TitleSkeleton />
      <PosSkeleton />
      <TitleSkeleton />
      <PosSkeleton />
      <PosSkeleton />
    </Stack>
  );
};
